import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { addMaterial } from '../../services/EntityService';
import { handleFormValidationErrors, showToast } from '../../utils/utils';
import MaterialUploadForm from '../../components/Materials/MaterialUploadForm';
import { selectUserInformations } from '../../features/authSlice';
import { selectMaterialCreateError, selectMaterialFormErrors, resetSubmissionStatus } from '../../features/materials/materialSlice';

/**
 * Create Material Page
 * Full page component for creating/uploading materials
 * Uses special MaterialUploadForm for multiple file uploads
 */
const CreateMaterialPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectUserInformations);
  const { courseId } = useParams();
  
  const createError = useSelector(selectMaterialCreateError);
  const formErrors = useSelector(selectMaterialFormErrors);

  const [formDataAdd, setFormDataAdd] = useState({
    course_id: courseId ? parseInt(courseId) : null,
    materials: [],
  });
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (formData) => {
    setIsLoading(true);
    try {
      const data = await addMaterial(formData, (progressEvent) => {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        setUploadProgress(percentCompleted);
      }, user.token);

      if (data.status === 200) {
        showToast('Materials Upload', data.message, 'success');
        dispatch(resetSubmissionStatus());
        navigate(courseId ? `/dashboard/courses/${courseId}/material` : '/dashboard/materials', { replace: true });
      }
    } catch (error) {
      handleFormValidationErrors(error, () => {}, () => {});
      showToast('Error', error.response?.data?.message || 'Failed to upload materials', 'error');
    } finally {
      setIsLoading(false);
      setUploadProgress(0);
    }
  };

  useEffect(() => {
    return () => {
      dispatch(resetSubmissionStatus());
    };
  }, [dispatch]);

  return (
    <div className="min-h-screen py-4 px-4 sm:px-6 lg:px-8">
      <div className="max-w-full mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                Upload Materials
              </h1>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Upload course materials below
              </p>
            </div>
            <button
              onClick={() => navigate(courseId ? `/dashboard/courses/${courseId}/material` : '/dashboard/materials')}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
          </div>
        </motion.div>

        {(createError || Object.values(formErrors).some(Boolean)) && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg dark:bg-red-900/20 dark:border-red-800"
          >
            {createError && (
              <p className="text-red-600 dark:text-red-400 text-sm font-medium mb-2">
                {createError}
              </p>
            )}
            {Object.entries(formErrors).map(([field, errorMsg]) => (
              errorMsg && (
                <p key={field} className="text-red-600 dark:text-red-400 text-sm">
                  {field}: {errorMsg}
                </p>
              )
            ))}
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6 lg:p-8 relative" style={{ overflow: 'visible' }}
        >
          <MaterialUploadForm
            onSubmit={handleSubmit}
            loading={isLoading}
            progress={uploadProgress}
            formDataAdd={formDataAdd}
            setFormDataAdd={setFormDataAdd}
            coursesDisabled={!!courseId}
          />
        </motion.div>
      </div>
    </div>
  );
};

export default CreateMaterialPage;
